import MapboxDraw, { DrawCustomMode, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import { defer } from "lodash";
import { SnapLineMode } from "mapbox-gl-draw-snap-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import { LayerIds } from "fond/types";
import { pairwise } from "fond/utils/array";

import { pullInFeatures, splitPaths } from "./helper";

const { constants: Constants } = MapboxDraw;

/**
 * This is a custom mode for drawing spans in the aerial planner.
 * It extends the draw_line_string mode and adds functionality for snapping to poles.
 */
const drawPoint: DrawCustomMode = {
  ...SnapLineMode,

  onStop: function (state) {
    const newFeature = this.getFeature(state.line.id) as DrawLineString;
    const newFeatures: DrawFeature[] = [];

    if (newFeature) {
      const coords = newFeature.getCoordinates().slice(0, -1);

      // Create new spans based on each pair of vertices
      pairwise(coords).forEach((coordinates) => {
        newFeatures.push(
          this.newFeature({
            type: Constants.geojsonTypes.FEATURE,
            properties: { layerId: LayerIds.inSpan },
            geometry: {
              type: Constants.geojsonTypes.LINE_STRING,
              coordinates,
            },
          }) as DrawLineString
        );
      });

      // Create new poles at each vertex
      // and delete any existing poles at the same location
      // and split any spans the new poles are collocated with
      coords.forEach((coordinates) => {
        const newPole = this.newFeature({
          type: Constants.geojsonTypes.FEATURE,
          properties: { layerId: LayerIds.inPole, [DRAWING_CONSTANTS.Unsnapped]: "false" },
          geometry: {
            type: Constants.geojsonTypes.POINT,
            coordinates,
          },
        }) as DrawPoint;

        // Delete any existing poles at the same location
        const { poles, spans } = pullInFeatures.call(this, newPole);
        poles.forEach((pole) => this.deleteFeature(pole.id.toString(), { silent: true }));
        newFeatures.push(newPole);

        // Split any spans the new poles are collocated with
        spans
          .filter(({ id }) => id !== newFeature.id)
          .forEach((span) => {
            splitPaths.call(this, span, newPole);
          });
      });
    }

    this.deleteFeature(state.line.id, { silent: true });

    newFeatures.forEach((f) => {
      this.addFeature(f);

      defer(() => {
        // Set the id property value to that of the id of the feature set by MapboxDraw.
        f.setProperty("id", f.id);
      });
    });

    this.map.fire("draw.create", { features: newFeatures.map((f) => f.toGeoJSON()) });

    // Call the original SnapLineMode onStop to handle removing of guide features
    // and any other cleanup such as map event listeners.
    SnapLineMode.onStop?.call(this, state);
  },
};

export default drawPoint;
